import { Icon, Spinner, SpinnerSize, Tab } from "@blueprintjs/core";
import { showError, useApiEffect } from "nsitools-react";
import * as React from "react";
import { useQuery } from "react-query";
import { useHistory, useParams } from "react-router";

import { BroodRequestApi, ERequestState, ERequestType, ErrorDetails, RequestApi } from "../../../../api";
import {
  BackButton,
  ConditionTab,
  LinkedSubFilesTab,
  PageBase,
  ProtectedTabs,
  RequestHistoryTab,
  RequestStickyMenu,
  VerificationTab,
} from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { getRouteStr } from "../../../../utils/RequestRouteUtil";
import { getSearchRouteStr } from "../../../../utils/RequestSearchRouteUtil";
import { MailLogListPage } from "../../../mail-log/MailLogListPage";
import { BillTab } from "../../BillTab";
import { BroodRequestDeclaration } from "./BroodRequestDeclaration";
import { BroodRequestDetail } from "./BroodRequestDetail";
import { DogValidationTab } from "./DogValidationTab";
import { PuppiesTab } from "./PuppiesTab";
import { PuppyGenealogyPage } from "./PuppyGenealogyPage";
import { DogInbreedingVerificationTab } from "./DogInbreedingVerificationTab";

export interface IBroodRequestItemPageProps {}

export const BroodRequestItemPage: React.FunctionComponent<IBroodRequestItemPageProps> = (props) => {
  const {
    id,
    tab,
    state,
    masterRequestId = "0",
  } = useParams<{
    id: string;
    tab: string;
    state: string;
    masterRequestId: string;
  }>();

  const { t, tUnsafe } = useTl();
  const history = useHistory();
  const broodRequestApi = useApiService(BroodRequestApi);
  const requestApi = useApiService(RequestApi);
  const [isRefreshed, setIsRefreshed] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const { currentLanguage } = useGlobalData();

  const [requestInfo, requestInfoLoading, , refreshRequestInfo] = useApiEffect(
    async () =>
      await requestApi.getRequestInfo(ERequestType.BroodRequest, +id, +masterRequestId, currentLanguage.languageId),
    [requestApi, id, masterRequestId, currentLanguage]
  );

  const refreshRequest = React.useCallback(() => {
    refreshRequestInfo();
    setIsRefreshed(true);
  }, [refreshRequestInfo]);

  const closeRequest = React.useCallback(() => {
    try {
      broodRequestApi.closeRequest(requestInfo?.parentRequestId);
    } catch (err) {
      const details: ErrorDetails = JSON.parse(err.response);
      showError(tUnsafe(`BackendError_${details.errorCode}`));
    }
  }, [broodRequestApi, requestInfo?.parentRequestId, tUnsafe]);

  const { data: isGenealogyComplete, isFetching: loadingIsGenealogyComplete } = useQuery(
    ["isGenealogyComplete", requestInfo?.parentRequestId],
    React.useCallback(
      async () => await broodRequestApi.isGenealogyComplete(+id),
      [broodRequestApi, requestInfo?.parentRequestId]
    )
  );

  const { data: isSrshPedigreeWanted, isFetching: loadingisSrshPedigreeWanted } = useQuery(
    ["isSrshPedigreeWanted", requestInfo?.parentRequestId],
    React.useCallback(
      async () => await broodRequestApi.isSrshPedigreeWanted(+id),
      [broodRequestApi, requestInfo?.parentRequestId]
    )
  );
  const { data: isBrcsRequest, isFetching: loadingIsBrcsRequest } = useQuery(
    ["isBrcsRequest", requestInfo?.parentRequestId],
    React.useCallback(
      async () => await broodRequestApi.isBrcsRequest(+id),
      [broodRequestApi, requestInfo?.parentRequestId]
    )
  );

  const breadCrumbs = React.useMemo(() => {
    let bc = [
      {
        text: t(ETLCodes.BroodRequests),
        route: getSearchRouteStr(requestInfo?.requestTypeCode),
      },
      {
        text: requestInfoLoading ? (
          <Spinner size={SpinnerSize.SMALL} />
        ) : +requestInfo?.requestId === 0 ? (
          t(ETLCodes.New)
        ) : (
          requestInfo?.requestNumber
        ),
      },
    ];

    if (requestInfo?.masterRequestId > 0) {
      bc = [
        {
          text: requestInfo?.masterRequestDisplayName,
          route: getSearchRouteStr(requestInfo?.masterRequestTypeCode),
        },
        {
          text: requestInfo?.masterRequestNumber,
          route: getRouteStr(requestInfo?.masterRequestTypeCode, requestInfo?.masterSubRequestId, "view"),
        },
        ...bc,
      ];
    }
    return bc;
  }, [requestInfo, requestInfoLoading, t]);

  return (
    <PageBase
      breadCrumbs={breadCrumbs}
      actionHistory={
        requestInfo != null && {
          tlCode: ETLCodes.ActionHistoryBroodRequest,
          name: requestInfo?.requestNumber,
          id: +id,
        }
      }
      stickyContent={() => {
        if (requestInfo?.requestId > 0 || requestInfo?.masterRequestId > 0) {
          return (
            <RequestStickyMenu
              requestId={requestInfo?.masterRequestId > 0 ? requestInfo?.masterRequestId : requestInfo?.parentRequestId}
              requestInfo={requestInfo}
              refreshFunc={refreshRequest}
              closeFunc={closeRequest}
              isLoading={(loading) => setIsLoading(loading)}
            />
          );
        } else {
          return null;
        }
      }}
    >
      <ProtectedTabs
        id="TabsBroodRequest"
        onChange={(newTabId) => history.push(`${ERoutes.broodRequest}/${+id}/${newTabId}/${state}`)}
        selectedTabId={tab}
        renderActiveTabPanelOnly
      >
        <BackButton backRoute={ERoutes.broodRequest} />
        <Tab
          id="detail"
          title={
            <div>
              {t(ETLCodes.Detail)} {requestInfo?.hasWarning && <Icon icon="warning-sign" intent="danger" />}
            </div>
          }
          panel={<BroodRequestDetail broodRequestId={+id} editMode={state === "edit"} requestInfo={requestInfo} />}
        />

        <Tab
          id="dogValidation"
          title={
            <div>
              {t(ETLCodes.GestationDeclaration)}{" "}
              {!isGenealogyComplete && !loadingIsGenealogyComplete && <Icon icon="warning-sign" intent="danger" />}
            </div>
          }
          panel={<DogValidationTab broodRequestId={+id} editMode={state === "edit"} />}
          disabled={requestInfo?.requestStateCode === ERequestState.REQUESTED}
        />
        {isBrcsRequest && (
          <Tab
            id="dogInbreedingConfirmation"
            title={t(ETLCodes.DogInbreedingConfirmation)}
            panel={<DogInbreedingVerificationTab broodRequestId={+id} redirectOnSave isDrupal={false} />}
            disabled={requestInfo?.parentRequestId <= 0}
          />
        )}

        <Tab
          id="broodRequestDeclaration"
          title={
            <div>
              {t(ETLCodes.BroodRequestDeclaration)}
              {!isSrshPedigreeWanted && !loadingisSrshPedigreeWanted && <Icon icon="warning-sign" intent="danger" />}
            </div>
          }
          panel={<BroodRequestDeclaration broodRequestId={+id} editMode={state === "edit"} requestInfo={requestInfo} />}
          disabled={
            requestInfo?.requestStateCode === ERequestState.REQUESTED ||
            requestInfo?.requestStateCode === ERequestState.DOG_VALIDATION
          }
        />
        <Tab
          id="verifications"
          title={t(ETLCodes.Verifications)}
          panel={
            requestInfoLoading ? (
              <Spinner />
            ) : (
              <VerificationTab
                requestId={requestInfo.parentRequestId}
                editMode={state === "edit"}
                statusETLCode={ETLCodes.VerificationsToObtainDogValidationStatus}
              />
            )
          }
          disabled={+id < 1}
        />
        <Tab
          id="conditions"
          title={t(ETLCodes.Conditions)}
          disabled={+id <= 0}
          panel={
            requestInfo ? (
              <ConditionTab
                requestId={requestInfo.parentRequestId}
                requestState={requestInfo?.requestStateCode}
                editMode={state === "edit"}
                statusETLCode={ETLCodes.Conditions}
              />
            ) : null
          }
        />
        <Tab
          id="puppies"
          title={t(ETLCodes.Puppies)}
          disabled={
            requestInfo?.requestStateCode !== ERequestState.PUPPIES_COMPLETE &&
            requestInfo?.requestStateCode !== ERequestState.LAB_WORK &&
            requestInfo?.requestStateCode !== ERequestState.EXECUTED_AWAITING_DOCUMENTS &&
            requestInfo?.requestStateCode !== ERequestState.EXECUTED
          }
          panel={
            <PuppiesTab
              requestId={requestInfo?.parentRequestId}
              isExecuted={requestInfo?.requestStateCode === ERequestState.EXECUTED}
              broodRequestId={requestInfo?.requestId}
              editMode={
                state === "edit" &&
                requestInfo?.requestStateCode !== ERequestState.EXECUTED &&
                requestInfo?.requestStateCode !== ERequestState.EXECUTED_AWAITING_DOCUMENTS
              }
              refreshPuppies={isRefreshed}
              isRefreshed={(c) => setIsRefreshed(!c)}
              isLoading={isLoading}
            />
          }
        />
        <Tab
          id="filiation"
          title={t(ETLCodes.Filiation)}
          disabled={+id <= 0}
          panel={<PuppyGenealogyPage broodRequestId={+id} />}
        />
        <Tab
          id="subrequests"
          title={t(ETLCodes.SubRequests, {
            count: requestInfoLoading ? `` : `(${requestInfo?.subRequestCount})`,
          })}
          panel={
            requestInfoLoading ? (
              <Spinner />
            ) : (
              <LinkedSubFilesTab requestInfo={requestInfo} editMode={state === "edit"} />
            )
          }
        />
        <Tab
          id="billing"
          title={t(ETLCodes.Billing)}
          panel={
            <BillTab
              requestId={requestInfo?.parentRequestId}
              disabled={requestInfo?.requestStateCode !== ERequestState.CANCELLED}
            />
          }
          disabled={
            requestInfo?.requestStateCode === ERequestState.REQUESTED ||
            requestInfo?.requestStateCode === ERequestState.DOG_VALIDATION ||
            requestInfo?.requestStateCode === ERequestState.GESTATION
          }
        />
        <Tab
          id="history"
          title={t(ETLCodes.History)}
          panel={requestInfoLoading ? <Spinner /> : <RequestHistoryTab requestId={requestInfo?.parentRequestId} />}
          disabled={+id <= 0}
        />
        <Tab
          id="mailing"
          title={t(ETLCodes.Email)}
          panel={requestInfoLoading ? <Spinner /> : <MailLogListPage requestId={requestInfo?.parentRequestId} />}
          disabled={+id <= 0}
        />
      </ProtectedTabs>
    </PageBase>
  );
};
